import React, { useEffect, useMemo, useState } from "react";
import { de } from "date-fns/locale";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DateRangePicker } from "react-date-range";
import moment from "moment";

import type { StayDetail } from "../../services/restserver-openapi/models/StayDetail";
import "./EditStayCalendar/editStayCalenderStyles.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

declare const H: any;

const CUSTOM_STAY_TYPE = {
    CUSTOM_START: "CUSTOM_START" as StayDetail.stayType,
    CUSTOM_STOP: "CUSTOM_STOP" as StayDetail.stayType,
    CUSTOM_END: "CUSTOM_END" as StayDetail.stayType,
};

type CustomStayDialogProps = {
    open: boolean;
    title: string;
    initialStayType?: StayDetail.stayType | null;
    unavailableTypes?: StayDetail.stayType[];
    initialStay?: StayDetail | null;
    onClose: () => void;
    onSave: (payload: {
        stayType: StayDetail.stayType;
        customTitle?: string;
        customDescription?: string;
        customAddressLine1: string;
        customAddressLine2?: string;
        customZipCode: string;
        customCity: string;
        customCoordinatesX: number;
        customCoordinatesY: number;
        fromDate?: number;
        toDate?: number;
    }) => Promise<void>;
};

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

function toPickerDate(timestamp?: number): Date {
    return new Date(timestamp ?? Date.now());
}

function normalizeToUtcStartOfDay(date: Date): number {
    return moment(date).add(12, "hours").utc().startOf("day").valueOf();
}

export default function CustomStayDialog({ open, title, initialStayType, unavailableTypes = [], initialStay, onClose, onSave }: CustomStayDialogProps) {
    const HERE_MAP_ID = process.env.REACT_APP_HERE_MAP_ID!;
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [selectedStayType, setSelectedStayType] = useState<StayDetail.stayType>(initialStayType ?? CUSTOM_STAY_TYPE.CUSTOM_STOP);
    const [customTitle, setCustomTitle] = useState("");
    const [customDescription, setCustomDescription] = useState("");
    const [customAddressLine1, setCustomAddressLine1] = useState("");
    const [customAddressLine2, setCustomAddressLine2] = useState("");
    const [customZipCode, setCustomZipCode] = useState("");
    const [customCity, setCustomCity] = useState("");
    const [customCoordinatesX, setCustomCoordinatesX] = useState<number | null>(null);
    const [customCoordinatesY, setCustomCoordinatesY] = useState<number | null>(null);
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(Date.now() + ONE_DAY_IN_MS),
            key: "selection",
        },
    ]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isBoundaryStay = selectedStayType === CUSTOM_STAY_TYPE.CUSTOM_START || selectedStayType === CUSTOM_STAY_TYPE.CUSTOM_END;

    const selectedStartDate = dateRange[0].startDate;
    const selectedEndDate = dateRange[0].endDate;

    const selectedDateLabel = useMemo(() => {
        if (isBoundaryStay) {
            return null;
        }

        return `${moment(selectedStartDate).format("DD.MM.YYYY")} - ${moment(selectedEndDate).format("DD.MM.YYYY")}`;
    }, [isBoundaryStay, selectedEndDate, selectedStartDate]);

    useEffect(() => {
        setSelectedStayType(initialStay?.stayType ?? initialStayType ?? CUSTOM_STAY_TYPE.CUSTOM_STOP);
        setCustomTitle(initialStay?.customTitle ?? "");
        setCustomDescription(initialStay?.customDescription ?? "");
        setCustomAddressLine1(initialStay?.customAddressLine1 ?? "");
        setCustomAddressLine2(initialStay?.customAddressLine2 ?? "");
        setCustomZipCode(initialStay?.customZipCode ?? "");
        setCustomCity(initialStay?.customCity ?? "");
        setCustomCoordinatesX(initialStay?.customCoordinatesX ?? null);
        setCustomCoordinatesY(initialStay?.customCoordinatesY ?? null);
        const startDate = toPickerDate(initialStay?.fromDate);
        const endDate = initialStay?.toDate != null
            ? toPickerDate(initialStay.toDate)
            : new Date(startDate.getTime() + ONE_DAY_IN_MS);

        setDateRange([
            {
                startDate,
                endDate: endDate.getTime() <= startDate.getTime() ? new Date(startDate.getTime() + ONE_DAY_IN_MS) : endDate,
                key: "selection",
            },
        ]);
        setError(null);
    }, [initialStay, initialStayType, open]);

    const handleDateRangeChange = (rangesByKey: { [key: string]: { startDate?: Date; endDate?: Date } }) => {
        const selection = rangesByKey.selection;
        const nextStartDate = selection.startDate ?? selectedStartDate;
        const rawEndDate = selection.endDate ?? selectedEndDate;
        const nextEndDate = rawEndDate.getTime() <= nextStartDate.getTime()
            ? new Date(nextStartDate.getTime() + ONE_DAY_IN_MS)
            : rawEndDate;

        setDateRange([
            {
                startDate: nextStartDate,
                endDate: nextEndDate,
                key: "selection",
            },
        ]);
    };

    const handleAddressChange = (setter: (value: string) => void, value: string) => {
        setter(value);
        setCustomCoordinatesX(null);
        setCustomCoordinatesY(null);
    };

    const generateCoordinates = async (): Promise<{ x: number; y: number }> => {
        return new Promise((resolve, reject) => {
            try {
                const platform = new H.service.Platform({ apikey: HERE_MAP_ID });
                const service = platform.getSearchService();
                const addressQuery = [customAddressLine1, customAddressLine2, `${customZipCode} ${customCity}`]
                    .filter((value) => value.trim().length > 0)
                    .join(", ");

                service.geocode(
                    { q: addressQuery },
                    (result: any) => {
                        if (!result.items?.length) {
                            reject(new Error("No coordinates found"));
                            return;
                        }

                        const position = result.items[0].position;
                        resolve({ x: position.lat, y: position.lng });
                    },
                    (geoError: Error) => reject(geoError)
                );
            } catch (geoError) {
                reject(geoError);
            }
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            setError(null);
            let coordinatesX = customCoordinatesX;
            let coordinatesY = customCoordinatesY;

            if (coordinatesX == null || coordinatesY == null) {
                const coordinates = await generateCoordinates();
                coordinatesX = coordinates.x;
                coordinatesY = coordinates.y;
                setCustomCoordinatesX(coordinates.x);
                setCustomCoordinatesY(coordinates.y);
            }

            await onSave({
                stayType: selectedStayType,
                customTitle: customTitle.trim() || undefined,
                customDescription: customDescription.trim() || undefined,
                customAddressLine1: customAddressLine1.trim(),
                customAddressLine2: customAddressLine2.trim() || undefined,
                customZipCode: customZipCode.trim(),
                customCity: customCity.trim(),
                customCoordinatesX: coordinatesX,
                customCoordinatesY: coordinatesY,
                fromDate: isBoundaryStay ? undefined : normalizeToUtcStartOfDay(selectedStartDate),
                toDate: isBoundaryStay ? undefined : normalizeToUtcStartOfDay(selectedEndDate),
            });
            onClose();
        } catch (saveError) {
            console.error("Saving custom stay failed", saveError);
            if (saveError instanceof Error && saveError.message === "CUSTOM_STAY_OVERLAP_CANCELLED") {
                return;
            }
            setError("Der Reisepunkt konnte nicht gespeichert werden.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        select
                        label="Typ"
                        value={selectedStayType}
                        onChange={(event) => setSelectedStayType(event.target.value as StayDetail.stayType)}
                        fullWidth
                        size="small"
                        disabled={!!initialStay}
                    >
                        <MenuItem value={CUSTOM_STAY_TYPE.CUSTOM_START} disabled={!initialStay && unavailableTypes.includes(CUSTOM_STAY_TYPE.CUSTOM_START)}>Startort</MenuItem>
                        <MenuItem value={CUSTOM_STAY_TYPE.CUSTOM_STOP}>Zwischenstopp</MenuItem>
                        <MenuItem value={CUSTOM_STAY_TYPE.CUSTOM_END} disabled={!initialStay && unavailableTypes.includes(CUSTOM_STAY_TYPE.CUSTOM_END)}>Zielort</MenuItem>
                    </TextField>
                    <TextField
                        label="Titel"
                        value={customTitle}
                        onChange={(event) => setCustomTitle(event.target.value)}
                        fullWidth
                        size="small"
                        placeholder="z. B. Bei Tante Anna"
                    />
                    <TextField
                        label="Adresse"
                        value={customAddressLine1}
                        onChange={(event) => handleAddressChange(setCustomAddressLine1, event.target.value)}
                        fullWidth
                        size="small"
                        required
                    />
                    <TextField
                        label="Adresszusatz"
                        value={customAddressLine2}
                        onChange={(event) => handleAddressChange(setCustomAddressLine2, event.target.value)}
                        fullWidth
                        size="small"
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            label="PLZ"
                            value={customZipCode}
                            onChange={(event) => handleAddressChange(setCustomZipCode, event.target.value)}
                            fullWidth
                            size="small"
                            required
                        />
                        <TextField
                            label="Ort"
                            value={customCity}
                            onChange={(event) => handleAddressChange(setCustomCity, event.target.value)}
                            fullWidth
                            size="small"
                            required
                        />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        {customCoordinatesX != null && customCoordinatesY != null
                            ? `Lat ${customCoordinatesX.toFixed(5)}, Lng ${customCoordinatesY.toFixed(5)}`
                            : "Koordinaten werden beim Speichern automatisch aus der Adresse ermittelt"}
                    </Typography>
                    {!isBoundaryStay && (
                        <Stack spacing={1.5}>
                            <Typography variant="body2" color="text.secondary">
                                Reisezeitraum{selectedDateLabel ? `: ${selectedDateLabel}` : ""}
                            </Typography>
                            <DateRangePicker
                                showPreview={false}
                                locale={de}
                                ranges={dateRange}
                                onChange={handleDateRangeChange}
                                showMonthAndYearPickers={false}
                                months={isSmallScreen ? 1 : 2}
                                direction="horizontal"
                                showDateDisplay={false}
                                moveRangeOnFirstSelection={false}
                                retainEndDateOnFirstSelection
                                className="date-range-picker"
                                rangeColors={[theme.palette.mode === "light" ? theme.palette.secondary.main : theme.palette.primary.dark]}
                            />
                            <Typography variant="caption" color="text.secondary">
                                Das Bis-Datum liegt immer nach dem Von-Datum.
                            </Typography>
                        </Stack>
                    )}
                    <TextField
                        label="Beschreibung"
                        value={customDescription}
                        onChange={(event) => setCustomDescription(event.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                    />
                    {error && (
                        <Typography variant="caption" color="error">
                            {error}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving || !customAddressLine1.trim() || !customZipCode.trim() || !customCity.trim()}
                    >
                        Speichern
                    </Button>
            </DialogActions>
        </Dialog>
    );
}
